import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Ticket, MessageSquare, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

export default function NotificationBell({ prominent = false }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const lastCheckRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const res = await api.getNotifications(60); // Last hour
      setNotifications(res.data);
      lastCheckRef.current = new Date();
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalCount = notifications?.total_new || 0;
  const newTickets = notifications?.new_tickets || [];
  const newReplies = notifications?.new_replies || [];

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const handleTicketClick = (ticketId) => {
    setOpen(false);
    navigate(`/tickets/managed/${ticketId}`);
  };

  // Prominent style for main header placement - bell icon with badge
  if (prominent) {
    return (
      <div className="relative" ref={dropdownRef}>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2"
          onClick={() => setOpen(!open)}
          data-testid="notification-bell"
          title="Notifications"
        >
          <Bell className={`h-5 w-5 ${totalCount > 0 ? 'text-red-600' : 'text-slate-500'}`} />
          {totalCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {totalCount > 9 ? "9+" : totalCount}
              </span>
            </span>
          )}
        </Button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
              <span className="font-medium text-sm text-gray-700">Notifications</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="max-h-96 overflow-y-auto">
              {totalCount === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No new notifications</p>
                  <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                </div>
              ) : (
                <>
                  {/* New Tickets Section */}
                  {newTickets.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 bg-blue-50 border-b">
                        <span className="text-xs font-medium text-blue-700 flex items-center gap-1">
                          <Ticket className="h-3 w-3" />
                          New Tickets ({newTickets.length})
                        </span>
                      </div>
                      {newTickets.map((ticket) => (
                        <div
                          key={ticket.ticket_id}
                          onClick={() => handleTicketClick(ticket.ticket_id)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600">#{ticket.ticket_number}</span>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                  New
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate mt-0.5">{ticket.summary}</p>
                              <p className="text-xs text-gray-500 mt-0.5">From: {ticket.created_by_name}</p>
                            </div>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {formatTime(ticket.created_on)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* New Replies Section */}
                  {newReplies.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 bg-green-50 border-b">
                        <span className="text-xs font-medium text-green-700 flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          New Replies ({newReplies.length})
                        </span>
                      </div>
                      {newReplies.map((reply, idx) => (
                        <div
                          key={`${reply.ticket_id}-${idx}`}
                          onClick={() => handleTicketClick(reply.ticket_id)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600">#{reply.ticket_number}</span>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700 border-green-200">
                                  Reply
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate mt-0.5">{reply.summary}</p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">
                                {reply.reply_from}: {reply.reply_preview}
                              </p>
                            </div>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {formatTime(reply.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 bg-gray-50 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-blue-600 hover:text-blue-700"
                onClick={() => {
                  setOpen(false);
                  navigate("/tickets/managed");
                }}
              >
                View All Tickets
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Original compact style
  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative p-2"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className={`h-5 w-5 ${totalCount > 0 ? 'text-blue-600' : 'text-gray-500'}`} />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {totalCount > 9 ? "9+" : totalCount}
            </span>
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
            <span className="font-medium text-sm text-gray-700">Notifications</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {totalCount === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No new notifications</p>
                <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <>
                {/* New Tickets Section */}
                {newTickets.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-blue-50 border-b">
                      <span className="text-xs font-medium text-blue-700 flex items-center gap-1">
                        <Ticket className="h-3 w-3" />
                        New Tickets ({newTickets.length})
                      </span>
                    </div>
                    {newTickets.map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        onClick={() => handleTicketClick(ticket.ticket_id)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-600">#{ticket.ticket_number}</span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                New
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-800 truncate mt-0.5">{ticket.summary}</p>
                            <p className="text-xs text-gray-500 mt-0.5">From: {ticket.created_by_name}</p>
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" />
                            {formatTime(ticket.created_on)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Replies Section */}
                {newReplies.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-green-50 border-b">
                      <span className="text-xs font-medium text-green-700 flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        New Replies ({newReplies.length})
                      </span>
                    </div>
                    {newReplies.map((reply, idx) => (
                      <div
                        key={`${reply.ticket_id}-${idx}`}
                        onClick={() => handleTicketClick(reply.ticket_id)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-600">#{reply.ticket_number}</span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700 border-green-200">
                                Reply
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-800 truncate mt-0.5">{reply.summary}</p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">
                              {reply.reply_from}: {reply.reply_preview}
                            </p>
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" />
                            {formatTime(reply.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 bg-gray-50 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-blue-600 hover:text-blue-700"
              onClick={() => {
                setOpen(false);
                navigate("/tickets/managed");
              }}
            >
              View All Tickets
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
